import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Instagram, Facebook, Youtube } from 'lucide-react';

// Custom Reddit icon component (not available in lucide-react)
const RedditIcon = ({ size = 18 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
  </svg>
);

const LOGO_URL = '/logo.png';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-[#030312] border-t border-white/5 overflow-hidden" data-testid="footer">
      {/* Decorative gradient blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#005eff] rounded-full mix-blend-screen filter blur-[150px] opacity-[0.08]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#ffcd04] rounded-full mix-blend-screen filter blur-[150px] opacity-[0.06]" />
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-16 relative z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12 mb-12">
          {/* Brand - Takes more space */}
          <div className="lg:col-span-5">
            <Link to="/" data-testid="footer-logo" className="inline-block mb-6">
              <img src={LOGO_URL} alt="Jreez" className="h-16" />
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-6 max-w-md">
              We design and implement scalable growth ecosystems for service businesses. By combining performance marketing, conversion-focused creative, and AI-powered automation, we turn inconsistent efforts into structured, measurable growth.
            </p>
            
            {/* Social Links */}
            <div>
              <h5 className="text-white/80 text-sm font-semibold mb-4">Follow Us</h5>
              <div className="flex gap-3 flex-wrap">
                <a 
                  href="https://www.instagram.com/thebreezmedia/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#ffcd04] hover:text-black hover:border-[#ffcd04] transition-all"
                  data-testid="social-instagram"
                  aria-label="Instagram"
                >
                  <Instagram size={18} />
                </a>
                <a 
                  href="https://www.facebook.com/thebreezmedia.agency" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#ffcd04] hover:text-black hover:border-[#ffcd04] transition-all"
                  data-testid="social-facebook"
                  aria-label="Facebook"
                >
                  <Facebook size={18} />
                </a>
                <a 
                  href="https://www.linkedin.com/company/thebreezmedia" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#ffcd04] hover:text-black hover:border-[#ffcd04] transition-all"
                  data-testid="social-linkedin"
                  aria-label="LinkedIn"
                >
                  <Linkedin size={18} />
                </a>
                <a 
                  href="https://www.youtube.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#ffcd04] hover:text-black hover:border-[#ffcd04] transition-all"
                  data-testid="social-youtube"
                  aria-label="YouTube"
                >
                  <Youtube size={18} />
                </a>
                <a 
                  href="https://www.reddit.com/r/BreezMedia/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#ffcd04] hover:text-black hover:border-[#ffcd04] transition-all"
                  data-testid="social-reddit"
                  aria-label="Reddit"
                >
                  <RedditIcon size={18} />
                </a>
              </div>
            </div>
          </div>

          {/* Navigation Links - 3 columns */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Services</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/services" className="text-white/60 hover:text-[#ffcd04] transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1 h-1 rounded-full bg-[#ffcd04] opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  All Services
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-white/60 hover:text-[#ffcd04] transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1 h-1 rounded-full bg-[#ffcd04] opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  Marketing Systems
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-white/60 hover:text-[#ffcd04] transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1 h-1 rounded-full bg-[#ffcd04] opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  Website Development
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-white/60 hover:text-[#ffcd04] transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1 h-1 rounded-full bg-[#ffcd04] opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  AI Automation
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Company</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-white/60 hover:text-[#ffcd04] transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1 h-1 rounded-full bg-[#ffcd04] opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/case-studies" className="text-white/60 hover:text-[#ffcd04] transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1 h-1 rounded-full bg-[#ffcd04] opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  Case Studies
                </Link>
              </li>
              <li>
                <Link to="/how-we-work" className="text-white/60 hover:text-[#ffcd04] transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1 h-1 rounded-full bg-[#ffcd04] opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  How We Work
                </Link>
              </li>
              <li>
                <Link to="/funfacts" className="text-white/60 hover:text-[#ffcd04] transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1 h-1 rounded-full bg-[#ffcd04] opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  Fun Facts
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Get in Touch</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="mailto:info@thebreezmedia.com" 
                  className="flex items-start gap-3 text-white/60 hover:text-[#ffcd04] transition-colors group"
                >
                  <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#ffcd04]/10 group-hover:border-[#ffcd04]/30 transition-all">
                    <Mail size={16} className="text-[#ffcd04]" />
                  </div>
                  <div>
                    <div className="text-xs text-white/40 mb-1">Email Us</div>
                    <div className="text-sm">info@thebreezmedia.com</div>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href="tel:+919565571301" 
                  className="flex items-start gap-3 text-white/60 hover:text-[#ffcd04] transition-colors group"
                >
                  <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#ffcd04]/10 group-hover:border-[#ffcd04]/30 transition-all">
                    <Phone size={16} className="text-[#ffcd04]" />
                  </div>
                  <div>
                    <div className="text-xs text-white/40 mb-1">Call Us</div>
                    <div className="text-sm">+91 956-557-1301</div>
                  </div>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-white/60">
                  <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <MapPin size={16} className="text-[#ffcd04]" />
                  </div>
                  <div>
                    <div className="text-xs text-white/40 mb-1">Location</div>
                    <div className="text-sm">Remote-first agency</div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © {currentYear} The Breez Media. All rights reserved.
          </p>
          <div className="flex items-center gap-3 md:gap-6 flex-wrap justify-center">
            <Link to="/contact" className="text-white/40 hover:text-[#ffcd04] text-xs md:text-sm transition-colors whitespace-nowrap">
              Contact
            </Link>
            <span className="text-white/20">•</span>
            <Link to="/privacy-policy" className="text-white/40 hover:text-[#ffcd04] text-xs md:text-sm transition-colors whitespace-nowrap">
              Privacy Policy
            </Link>
            <span className="text-white/20">•</span>
            <Link to="/terms-of-service" className="text-white/40 hover:text-[#ffcd04] text-xs md:text-sm transition-colors whitespace-nowrap">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
